import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useBillingHistoryQuery } from 'src/api/subscription/useSubscriptionApi';
import { subscriptionService } from 'src/services/subscriptionService';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as ManagePaymentMethodCardSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/manage-payment-method-card.svg';
import { ReactComponent as ManagePaymentMethodNextSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/manage-payment-method-next.svg';
import { ReactComponent as DownloadAllInvoicesSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/download-all-invoices.svg';
import { ReactComponent as SearchSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/common/search.svg';
import { ReactComponent as PageArrowPreviousSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/page-arrow-previous.svg';
import { ReactComponent as PageArrowNextSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/page-arrow-next.svg';
import { toast } from 'src/utils/appAlert';
import './BillingHistory.scss';

export const BillingHistory: FC = () => {
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus] = useState('ALL');
  const [sortBy, setSortBy] = useState('NEWEST');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const pageSize = 7;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: historyData,
    isLoading,
    isFetching,
  } = useBillingHistoryQuery({
    page: currentPage,
    limit: pageSize,
    status: filterStatus === 'ALL' ? undefined : filterStatus.toLowerCase(),
    search: debouncedSearch || undefined,
    sort: sortBy.toLowerCase(),
  });

  const showItems = [
    { key: 'ALL', label: 'All Transactions' },
    { key: 'PAID', label: 'Paid' },
    { key: 'TRIAL', label: 'Free Trial' },
    { key: 'PENDING', label: 'Pending' },
    { key: 'PROCESSING', label: 'Processing' },
    { key: 'FAILED', label: 'Failed' },
    { key: 'REFUNDED', label: 'Refunded' },
    { key: 'CANCELLED', label: 'Cancelled' },
  ];

  const sortItems = [
    { key: 'NEWEST', label: 'Date (Newest)' },
    { key: 'OLDEST', label: 'Date (Oldest)' },
  ];

  const getShowLabel = () =>
    showItems.find((i) => i.key === filterStatus)?.label || 'All Transactions';
  const getSortLabel = () => sortItems.find((i) => i.key === sortBy)?.label || 'Date (Newest)';

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
    }
  };

  const handleGoToManagePayment = () => {
    navigate('/onboarding-trainer/dashboard/subscription/manage-payment-method');
  };

  const triggerCrossPlatformDownload = (url: string, filename: string) => {
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    window.setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
    }, 200);
  };

  const handleDownloadInvoice = async (invoiceId: string, invNum: string) => {
    try {
      setDownloadingId(invoiceId);
      toast.success(`Preparing invoice ${invNum}...`);
      const res = await subscriptionService.getInvoiceDownloadUrl(invoiceId);
      const url = res.download_url || res.invoice_pdf || res.hosted_invoice_url;
      if (url) {
        triggerCrossPlatformDownload(url, `${invNum || 'invoice'}.pdf`);
      } else {
        toast.error('Download URL is not available for this invoice.');
      }
    } catch (err: any) {
      toast.error(err.message || `Failed to download invoice ${invNum}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (isDownloadingAll) return;
    try {
      setIsDownloadingAll(true);
      const res = await subscriptionService.downloadAllInvoices();
      if (res.download_url) {
        triggerCrossPlatformDownload(res.download_url, 'all_invoices.zip');
      } else {
        toast.error('Download link is not available.');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to download all invoices.');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const paginatedInvoices = historyData?.invoices || historyData?.items || [];
  const totalResults =
    historyData?.total_count ?? historyData?.pagination?.total_items ?? paginatedInvoices.length;
  const totalPages =
    historyData?.total_pages ??
    historyData?.pagination?.total_pages ??
    Math.max(1, Math.ceil(totalResults / pageSize));
  const startIndex = (currentPage - 1) * pageSize;

  const isTableLoading = isLoading || isFetching;

  const getPaginationItems = (current: number, total: number): (number | string)[] => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    if (current <= 4) {
      return [1, 2, 3, 4, 5, '...', total];
    }

    if (current >= total - 3) {
      return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    }

    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  return (
    <div className="billing-history-page-container">
      {/* Header */}
      <div className="billing-history-header">
        <div className="header-titles">
          <button type="button" className="history-back-btn" onClick={handleBack}>
            <UserChevronLeftSVG />
            <span>{'Billing History'}</span>
          </button>
          <p className="history-subtitle">{'View your past payments, invoices, and receipts.'}</p>
        </div>

        <div className="header-actions">
          <button type="button" className="btn-manage-pay-header" onClick={handleGoToManagePayment}>
            <ManagePaymentMethodCardSVG />
            <span>{'Manage Payment Method'}</span>
            <ManagePaymentMethodNextSVG />
          </button>
          {totalResults > 0 && (
            <div className="download-all-container">
              <button
                type="button"
                className="btn-download-all-header"
                onClick={handleDownloadAll}
                disabled={isDownloadingAll}
              >
                {isDownloadingAll ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 16, color: '#060723' }} spin />}
                  />
                ) : (
                  <DownloadAllInvoicesSVG />
                )}
                <span>
                  {isDownloadingAll ? 'Preparing all invoices archive...' : 'Download All Invoices'}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="filters-search-bar">
        <div className="filter-dropdowns">
          <Dropdown
            menu={{
              items: showItems,
              onClick: (e) => {
                setFilterStatus(e.key);
                setCurrentPage(1);
              },
            }}
            trigger={['click']}
            rootClassName="dark-theme-dropdown"
          >
            <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
              <span>
                <span className="desktop-prefix">{'Show: '}</span>
                <span className="value">{getShowLabel()}</span>
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </Dropdown>

          <Dropdown
            menu={{
              items: sortItems,
              onClick: (e) => {
                setSortBy(e.key);
                setCurrentPage(1);
              },
            }}
            trigger={['click']}
            rootClassName="dark-theme-dropdown"
          >
            <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
              <span>
                <span className="desktop-prefix">{'Sort: '}</span>
                <span className="value">{getSortLabel()}</span>
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </Dropdown>
        </div>

        <div className="divider" />

        <div className="search-input">
          <SearchSVG />
          <input
            type="text"
            placeholder="Search invoices..."
            maxLength={100}
            aria-label="Search invoices"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            onBlur={(e) => setSearchQuery(e.target.value.trim())}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setCurrentPage(1);
              }}
              className="clear-search-btn"
              aria-label="Clear search"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Invoices Table Card */}
      <div className="invoices-table-card">
        {/* Desktop Table */}
        <table className="invoices-table">
          <thead>
            <tr>
              <th>{'Date'}</th>
              <th>{'Description'}</th>
              <th>{'Plan'}</th>
              <th>{'Amount'}</th>
              <th>{'Status'}</th>
              <th>{'Invoice'}</th>
              <th className="th-action"></th>
            </tr>
          </thead>
          <tbody>
            {isTableLoading ? (
              <tr>
                <td colSpan={7} className="td-loading">
                  <div className="table-loading-container">
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 28, color: '#E2F163' }} spin />
                      }
                    />
                    <span className="table-loading-text">{'Loading invoices...'}</span>
                  </div>
                </td>
              </tr>
            ) : paginatedInvoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="td-empty">
                  {'No billing transactions found.'}
                </td>
              </tr>
            ) : (
              paginatedInvoices.map((inv: any) => {
                const invId = inv.id;
                const invNum = inv.invoice_number || inv.invoiceNumber || inv.id;
                const invDate = inv.formatted_date || inv.date;
                const invDesc = inv.description;
                const invPeriod = inv.period;
                const invPlan = inv.plan_name || inv.plan || 'Pro Plan';
                const invAmount = inv.formatted_amount || inv.amount;
                const invStatus = inv.status;
                const statusText =
                  inv.status_label || (invStatus === 'TRIAL' ? 'Free Trial' : invStatus);
                const isDownloadDisabled =
                  (inv.is_trial || invStatus === 'TRIAL') && !inv.invoice_pdf && !inv.pdf_available;
                return (
                  <tr key={invId}>
                    <td className="td-date">{invDate}</td>
                    <td className="td-description">
                      <div className="desc-title">{invDesc}</div>
                      <div className="desc-sub">{invPeriod}</div>
                    </td>
                    <td className="td-plan">{invPlan}</td>
                    <td className="td-amount">{invAmount}</td>
                    <td className="td-status">
                      <span className={`status-badge ${String(invStatus).toLowerCase()}`}>
                        {statusText}
                      </span>
                    </td>
                    <td className="td-invoice-num">{invNum}</td>
                    <td className="td-download">
                      <button
                        type="button"
                        className="btn-download-row"
                        onClick={() => handleDownloadInvoice(invId, invNum)}
                        disabled={downloadingId === invId || isDownloadingAll || isDownloadDisabled}
                        title={
                          isDownloadDisabled
                            ? 'Free trial activation (No charge)'
                            : 'Download invoice'
                        }
                        aria-label={`Download invoice ${invNum}`}
                      >
                        {downloadingId === invId ? (
                          <Spin
                            indicator={
                              <LoadingOutlined style={{ fontSize: 15, color: '#E2F163' }} spin />
                            }
                          />
                        ) : (
                          <DownloadAllInvoicesSVG />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Mobile Cards List */}
        <div className="mobile-invoices-cards">
          {isTableLoading ? (
            <div className="mobile-loading-state">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 26, color: '#E2F163' }} spin />}
              />
              <span>{'Loading invoices...'}</span>
            </div>
          ) : paginatedInvoices.length === 0 ? (
            <div className="mobile-empty-state">{'No billing transactions found.'}</div>
          ) : (
            paginatedInvoices.map((inv: any) => {
              const invId = inv.id;
              const invNum = inv.invoice_number || inv.invoiceNumber || inv.id;
              const invDate = inv.formatted_date || inv.date;
              const invDesc = inv.description;
              const invPeriod = inv.period;
              const invAmount = inv.formatted_amount || inv.amount;
              const invStatus = inv.status;
              const statusText =
                inv.status_label || (invStatus === 'TRIAL' ? 'Free Trial' : invStatus);
              const isDownloadDisabled =
                (inv.is_trial || invStatus === 'TRIAL') && !inv.invoice_pdf && !inv.pdf_available;
              return (
                <div key={invId} className="mobile-invoice-card">
                  <div className="card-date-label">{invDate}</div>
                  <div className="card-row-main">
                    <span className="card-desc-title">{invDesc}</span>
                    <span className="card-amount">{invAmount}</span>
                  </div>
                  <div className="card-row-sub">
                    <span className="card-period">{invPeriod}</span>
                    <span className={`status-badge ${String(invStatus).toLowerCase()}`}>
                      {statusText}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn-mobile-download-invoice"
                    onClick={() => handleDownloadInvoice(invId, invNum)}
                    disabled={downloadingId === invId || isDownloadingAll || isDownloadDisabled}
                    title={
                      isDownloadDisabled ? 'Free trial activation (No charge)' : 'Download invoice'
                    }
                  >
                    <span>{invNum}</span>
                    {downloadingId === invId ? (
                      <Spin
                        indicator={
                          <LoadingOutlined style={{ fontSize: 15, color: '#E2F163' }} spin />
                        }
                      />
                    ) : (
                      <DownloadAllInvoicesSVG />
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Footer */}
        {totalResults > 0 && (
          <div className="table-pagination-footer">
            <span className="results-counter">
              {`Showing ${Math.min(startIndex + 1, totalResults)} to ${Math.min(
                startIndex + pageSize,
                totalResults,
              )} • ${totalResults} results`}
            </span>

            <div className="pagination-controls">
              <button
                type="button"
                className="page-nav-btn"
                disabled={currentPage <= 1 || isFetching}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                aria-label="Previous Page"
              >
                <PageArrowPreviousSVG />
              </button>

              {getPaginationItems(currentPage, totalPages).map((item, index) => {
                if (typeof item === 'string') {
                  return (
                    <span key={`ellipsis-${index}`} className="page-ellipsis">
                      {item}
                    </span>
                  );
                }
                return (
                  <button
                    key={item}
                    type="button"
                    className={`page-num-btn ${currentPage === item ? 'active' : ''}`}
                    onClick={() => setCurrentPage(item)}
                    disabled={isFetching}
                  >
                    {item}
                  </button>
                );
              })}

              <button
                type="button"
                className="page-nav-btn"
                disabled={currentPage >= totalPages || isFetching}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next Page"
              >
                <PageArrowNextSVG />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
